import { z } from 'zod';
import {
  REPORT_GENERATION_ENTITIES,
  REPORT_GENERATION_GALLERY_TEMPLATES,
  REPORT_GENERATION_ENTITY_JOINS,
  type ReportGenerationTemplateDefinition,
} from './reportGenerationSemanticCatalog';
import {
  RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS,
  RUN_REPORTS_UNSAFE_CHART_LABEL_FIELD_IDS,
  validateRunReportsVisualizationMappings,
  type RunReportsTemplateVisualizationMapping,
  type RunReportsVisualizationAggregate,
  type RunReportsVisualizationPrimarySurface,
} from './runReportsVisualizationMapping';
import {
  YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
  YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS,
} from './ystnjScholarshipWorksheetReport';

export const RUN_REPORTS_SEED_SCHEMA_VERSION = 'run-reports-seed.v1';
export const RUN_REPORTS_SEED_ARTIFACT_TYPE = 'run-reports-seed-bundle';

export const RUN_REPORTS_SEED_TARGET_APPS = ['intellio-demo', 'ystnj'] as const;
export type RunReportsSeedTargetApp = typeof RUN_REPORTS_SEED_TARGET_APPS[number];

export type RunReportsSeedPrimitive = string | number | boolean | null;

export type RunReportsSeedRecord = {
  id: string;
  entityId: string;
  joinKeys: Record<string, string>;
  cells: Record<string, RunReportsSeedPrimitive>;
  scenarioIds: string[];
  sourceTemplateIds: string[];
  visualTags: string[];
};

export type RunReportsSeedEntityPayload = {
  entityId: string;
  label: string;
  moduleLabel: string;
  routeRoot: string;
  records: RunReportsSeedRecord[];
};

export type RunReportsSeedTemplateCoverage = {
  templateId: string;
  templateName: string;
  moduleLabel: string;
  entityIds: string[];
  fieldIds: string[];
  expectedMinimumRows: number;
  coveredFieldIds: string[];
  missingFieldIds: string[];
  sampleRecordIds: string[];
  chartUseCaseIds: string[];
};

export type RunReportsSeedVisualCoverage = {
  id: string;
  label: string;
  templateMappingId: string;
  primarySurface: RunReportsVisualizationPrimarySurface;
  fallbackSurface: 'table';
  templateIds: string[];
  entityIds: string[];
  fieldIds: string[];
  chartTypes: string[];
  advancedVisuals: string[];
  expectedUseCaseId: string;
  aggregate: RunReportsVisualizationAggregate;
  dimensionFieldIds: string[];
  groupFieldIds: string[];
  requiredBucketFields: string[];
  requiredMetricFields: string[];
  visualEnrichmentFieldIds: string[];
  unsafeLabelFieldIds: string[];
  minBuckets: number;
  minSeries: number;
};

export type RunReportsSeedAssistantActionCoverage = {
  actionId: string;
  label: string;
  route: '/school/run-reports';
  requiredTemplateIds: string[];
  requiredEntityIds: string[];
  requiredFieldIds: string[];
  expectedRouteMutation: string;
  responseStructure: {
    kind: 'route-command';
    requiresWorkspaceMutation: boolean;
    receiptIsProof: false;
  };
};

export type RunReportsSeedBundle = {
  schemaVersion: typeof RUN_REPORTS_SEED_SCHEMA_VERSION;
  artifactType: typeof RUN_REPORTS_SEED_ARTIFACT_TYPE;
  generatedAt: string;
  runId: string;
  targetApps: RunReportsSeedTargetApp[];
  academicYears: string[];
  source: {
    catalogTemplateCount: number;
    catalogEntityCount: number;
    protocolRefs: string[];
    sourceHashes?: Record<string, string>;
  };
  entities: Record<string, RunReportsSeedEntityPayload>;
  templateCoverage: RunReportsSeedTemplateCoverage[];
  visualCoverage: RunReportsSeedVisualCoverage[];
  assistantActionCoverage: RunReportsSeedAssistantActionCoverage[];
  toolEntityAiMappings: Array<{
    mappingId: string;
    templateIds: string[];
    entityIds: string[];
    tableView: string;
    visualView: string;
    graphView: string;
    aiQuickActionIds: string[];
  }>;
  operationCoverage: Array<{
    operationId: string;
    boundedContext: string;
    transportField: string;
    seedEntityIds: string[];
    proofRequirement: string;
  }>;
  routeIntentCoverage: Array<{
    route: '/school/run-reports';
    module: 'run-reports';
    widget: string;
    visibleContract: string;
    backingEntities: string[];
    seedScenarioIds: string[];
  }>;
  nonProdWritePlan: {
    defaultMode: 'dry-run';
    guardEnv: 'RUN_REPORTS_SEED_ALLOW_WRITES';
    confirmationFlag: '--confirm';
    destructiveResetDefault: false;
    loadOrder: string[];
    appPlans: Array<{
      appId: RunReportsSeedTargetApp;
      status: 'adapter-ready' | 'handoff-required';
      writeSurface: string;
      readbackSurface: string;
      notes: string[];
    }>;
  };
  validation: RunReportsSeedValidationResult;
};

export type RunReportsSeedValidationIssue = {
  severity: 'error' | 'warning';
  code: string;
  message: string;
};

export type RunReportsSeedValidationResult = {
  status: 'PASS' | 'FAIL';
  issueCount: number;
  issues: RunReportsSeedValidationIssue[];
  summary: {
    templateCount: number;
    entityCount: number;
    recordCount: number;
    targetApps: RunReportsSeedTargetApp[];
  };
};

const SeedPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const RunReportsSeedBundleSchema = z.object({
  schemaVersion: z.literal(RUN_REPORTS_SEED_SCHEMA_VERSION),
  artifactType: z.literal(RUN_REPORTS_SEED_ARTIFACT_TYPE),
  generatedAt: z.string().min(1),
  runId: z.string().min(1),
  targetApps: z.array(z.enum(RUN_REPORTS_SEED_TARGET_APPS)).min(1),
  academicYears: z.array(z.string().min(1)).min(1),
  source: z.object({
    catalogTemplateCount: z.number().int().positive(),
    catalogEntityCount: z.number().int().positive(),
    protocolRefs: z.array(z.string().min(1)).min(1),
    sourceHashes: z.record(z.string(), z.string()).optional(),
  }),
  entities: z.record(z.string(), z.object({
    entityId: z.string().min(1),
    label: z.string().min(1),
    moduleLabel: z.string().min(1),
    routeRoot: z.string().min(1),
    records: z.array(z.object({
      id: z.string().min(1),
      entityId: z.string().min(1),
      joinKeys: z.record(z.string(), z.string()),
      cells: z.record(z.string(), SeedPrimitiveSchema),
      scenarioIds: z.array(z.string().min(1)),
      sourceTemplateIds: z.array(z.string().min(1)),
      visualTags: z.array(z.string().min(1)),
    })),
  })),
  templateCoverage: z.array(z.object({
    templateId: z.string().min(1),
    templateName: z.string().min(1),
    moduleLabel: z.string().min(1),
    entityIds: z.array(z.string().min(1)),
    fieldIds: z.array(z.string().min(1)),
    expectedMinimumRows: z.number().int().nonnegative(),
    coveredFieldIds: z.array(z.string().min(1)),
    missingFieldIds: z.array(z.string().min(1)),
    sampleRecordIds: z.array(z.string().min(1)),
    chartUseCaseIds: z.array(z.string().min(1)),
  })),
  visualCoverage: z.array(z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    templateMappingId: z.string().min(1),
    primarySurface: z.enum(['visual', 'table']),
    fallbackSurface: z.literal('table'),
    templateIds: z.array(z.string().min(1)),
    entityIds: z.array(z.string().min(1)),
    fieldIds: z.array(z.string().min(1)),
    chartTypes: z.array(z.string().min(1)),
    advancedVisuals: z.array(z.string().min(1)),
    expectedUseCaseId: z.string().min(1),
    aggregate: z.enum(['sum', 'average', 'count']),
    dimensionFieldIds: z.array(z.string().min(1)),
    groupFieldIds: z.array(z.string().min(1)),
    requiredBucketFields: z.array(z.string().min(1)),
    requiredMetricFields: z.array(z.string().min(1)),
    visualEnrichmentFieldIds: z.array(z.string().min(1)),
    unsafeLabelFieldIds: z.array(z.string().min(1)),
    minBuckets: z.number().int().nonnegative(),
    minSeries: z.number().int().nonnegative(),
  })),
  assistantActionCoverage: z.array(z.object({
    actionId: z.string().min(1),
    label: z.string().min(1),
    route: z.literal('/school/run-reports'),
    requiredTemplateIds: z.array(z.string().min(1)),
    requiredEntityIds: z.array(z.string().min(1)),
    requiredFieldIds: z.array(z.string().min(1)),
    expectedRouteMutation: z.string().min(1),
    responseStructure: z.object({
      kind: z.literal('route-command'),
      requiresWorkspaceMutation: z.literal(true),
      receiptIsProof: z.literal(false),
    }),
  })),
  toolEntityAiMappings: z.array(z.object({
    mappingId: z.string().min(1),
    templateIds: z.array(z.string().min(1)),
    entityIds: z.array(z.string().min(1)),
    tableView: z.string().min(1),
    visualView: z.string().min(1),
    graphView: z.string().min(1),
    aiQuickActionIds: z.array(z.string().min(1)),
  })),
  operationCoverage: z.array(z.object({
    operationId: z.string().min(1),
    boundedContext: z.string().min(1),
    transportField: z.string().min(1),
    seedEntityIds: z.array(z.string().min(1)),
    proofRequirement: z.string().min(1),
  })),
  routeIntentCoverage: z.array(z.object({
    route: z.literal('/school/run-reports'),
    module: z.literal('run-reports'),
    widget: z.string().min(1),
    visibleContract: z.string().min(1),
    backingEntities: z.array(z.string().min(1)),
    seedScenarioIds: z.array(z.string().min(1)),
  })),
  nonProdWritePlan: z.object({
    defaultMode: z.literal('dry-run'),
    guardEnv: z.literal('RUN_REPORTS_SEED_ALLOW_WRITES'),
    confirmationFlag: z.literal('--confirm'),
    destructiveResetDefault: z.literal(false),
    loadOrder: z.array(z.string().min(1)),
    appPlans: z.array(z.object({
      appId: z.enum(RUN_REPORTS_SEED_TARGET_APPS),
      status: z.enum(['adapter-ready', 'handoff-required']),
      writeSurface: z.string().min(1),
      readbackSurface: z.string().min(1),
      notes: z.array(z.string().min(1)),
    })),
  }),
  validation: z.object({
    status: z.enum(['PASS', 'FAIL']),
    issueCount: z.number().int().nonnegative(),
    issues: z.array(z.object({
      severity: z.enum(['error', 'warning']),
      code: z.string().min(1),
      message: z.string().min(1),
    })),
    summary: z.object({
      templateCount: z.number().int().nonnegative(),
      entityCount: z.number().int().nonnegative(),
      recordCount: z.number().int().nonnegative(),
      targetApps: z.array(z.enum(RUN_REPORTS_SEED_TARGET_APPS)),
    }),
  }),
}) satisfies z.ZodType<RunReportsSeedBundle>;

const PROTOCOL_REFS = [
  'documentation/RUN-REPORTS-SEED-DATA-PROTOCOL.md',
  'documentation/YST-UNIFIED-SEED-DATA-PROMPT.md',
  'documentation/AMPLIFY-SINGLE-HEAD-MULTI-SUB-API-PARADIGM.md',
  'packages/rtk-abstractions/documentation/testing/RTK-GLUE-VERIFICATION-GATES.md',
  'documentation/core/reference/visual-inspection/GRPO-VISUAL-INSPECTION-PROTOCOL.md',
];

const DENIED_DATA_PATTERNS = [
  /\bfake\b/i,
  /\bmock\b/i,
  /\bplaceholder\b/i,
  /\bseeded\b/i,
  /\be2e\b/i,
  /\bexample\.test\b/i,
  /\b555-\d{4}\b/i,
  /\bfam-demo-[a-z0-9-]+\b/i,
  /\btuition-seeded-[a-z0-9-]+\b/i,
  /\btuition-adler-\d+\b/i,
] as const;

const entityDefinitionById = new Map(REPORT_GENERATION_ENTITIES.map((entity) => [entity.id, entity]));

const familyProfiles = [
  ['rr-family-001', 'Arden', 'Iris', 'Noam', 'K', 'Lower School', 28400, 7200, 64000, 'Submitted', 'Needs W2', 'Signed'],
  ['rr-family-002', 'Benton', 'Mira', 'Eli', '1', 'Lower School', 31200, 0, 83000, 'Awarded', 'Complete', 'Signed'],
  ['rr-family-003', 'Carmel', 'Talia', 'Jonah', '2', 'Lower School', 33600, 18200, 101000, 'Review', 'Bank statements pending', 'Sent'],
  ['rr-family-004', 'Dagan', 'Lena', 'Ari', '3', 'Lower School', 34800, 5400, 126000, 'Submitted', 'Complete', 'Signed'],
  ['rr-family-005', 'Elion', 'Nava', 'Matan', '4', 'Lower School', 36200, 22600, 48000, 'Appeal', 'Appeal letter pending', 'Draft'],
  ['rr-family-006', 'Fenton', 'Ruth', 'Leah', '5', 'Middle School', 37400, 9100, 158000, 'Awarded', 'Complete', 'Signed'],
  ['rr-family-007', 'Geller', 'Sima', 'Yosef', '6', 'Middle School', 38600, 0, 73000, 'Denied', 'Complete', 'Declined'],
  ['rr-family-008', 'Harel', 'Dina', 'Avi', '7', 'Middle School', 39800, 14100, 93000, 'Review', 'Tax return pending', 'Sent'],
  ['rr-family-009', 'Ivri', 'Maya', 'Eden', '8', 'Middle School', 41200, 2600, 175000, 'Submitted', 'Complete', 'Signed'],
  ['rr-family-010', 'Jasper', 'Rina', 'Amir', '9', 'Upper School', 43800, 32100, 57000, 'Appeal', 'Committee review', 'Draft'],
  ['rr-family-011', 'Koren', 'Sara', 'Naomi', '10', 'Upper School', 45200, 11800, 112000, 'Awarded', 'Complete', 'Signed'],
  ['rr-family-012', 'Linden', 'Miriam', 'Ezra', '11', 'Upper School', 46800, 0, 139000, 'Submitted', 'Complete', 'Signed'],
  ['rr-family-013', 'Morgen', 'Alma', 'Yair', '12', 'Upper School', 48100, 27400, 69000, 'Review', 'Employer letter pending', 'Sent'],
  ['rr-family-014', 'Nerya', 'Orly', 'Shira', 'Pre-K', 'Early Childhood', 25800, 4100, 121000, 'Awarded', 'Complete', 'Signed'],
  ['rr-family-015', 'Orin', 'Hila', 'Rafi', 'K', 'Lower School', 28900, 16400, 88000, 'Submitted', 'Needs transcript', 'Sent'],
  ['rr-family-016', 'Paz', 'Yael', 'Mina', '6', 'Middle School', 39100, 8700, 147000, 'Denied', 'Complete', 'Expired'],
] as const;

const statusCycle = ['Open', 'Partial', 'Paid', 'Overdue'] as const;
const paymentMethods = ['ACH', 'Credit card', 'Check', 'Wire'] as const;
const awardStatuses = ['Committed', 'Pending', 'Appeal review', 'Declined'] as const;
const applicationStatuses = ['Inquiry', 'Applied', 'Documents pending', 'Interview scheduled', 'Accepted', 'Enrolled'] as const;

const dateFor = (monthIndex: number, day: number): string => {
  const month = String(((monthIndex % 12) + 1)).padStart(2, '0');
  return `2026-${month}-${String(day).padStart(2, '0')}`;
};

const record = (
  entityId: string,
  id: string,
  joinKeys: Record<string, string>,
  cells: Record<string, RunReportsSeedPrimitive>,
  scenarioIds: string[],
  visualTags: string[] = [],
): RunReportsSeedRecord => ({
  id,
  entityId,
  joinKeys,
  cells,
  scenarioIds,
  sourceTemplateIds: [],
  visualTags,
});

const pushRecord = (
  entities: Record<string, RunReportsSeedEntityPayload>,
  entry: RunReportsSeedRecord,
): void => {
  const entity = entities[entry.entityId];
  if (!entity) {
    throw new Error(`Unknown Run Reports seed entity: ${entry.entityId}`);
  }
  entity.records.push(entry);
};

const buildEmptyEntityPayloads = (): Record<string, RunReportsSeedEntityPayload> =>
  Object.fromEntries(REPORT_GENERATION_ENTITIES.map((entity) => [
    entity.id,
    {
      entityId: entity.id,
      label: entity.label,
      moduleLabel: entity.moduleLabel,
      routeRoot: entity.routeRoot,
      records: [],
    },
  ]));

const collectTemplateIdsForEntity = (entityId: string): string[] =>
  REPORT_GENERATION_GALLERY_TEMPLATES
    .filter((template) => template.entities.includes(entityId))
    .map((template) => template.id);

const attachTemplateIds = (entities: Record<string, RunReportsSeedEntityPayload>): void => {
  for (const payload of Object.values(entities)) {
    const templateIds = collectTemplateIdsForEntity(payload.entityId);
    for (const row of payload.records) {
      row.sourceTemplateIds = templateIds;
    }
  }
};

const buildSeedEntities = (): Record<string, RunReportsSeedEntityPayload> => {
  const entities = buildEmptyEntityPayloads();
  const studentIds: string[] = [];

  familyProfiles.forEach((profile, index) => {
    const [
      familyId,
      familyName,
      primaryFirstName,
      studentFirstName,
      gradeLevel,
      division,
      annualTuitionValue,
      balance,
      income,
      faStatus,
      docStatus,
      contractStatus,
    ] = profile;
    const phoneSuffix = String(1100 + index).padStart(4, '0');
    const studentId = `rr-student-${String(index + 1).padStart(3, '0')}`;
    const secondStudentId = `rr-student-${String(index + 17).padStart(3, '0')}`;
    const applicantId = `rr-applicant-${String(index + 1).padStart(3, '0')}`;
    const annualTuition = Number(annualTuitionValue);
    const accountBalance = Number(balance);
    const reportedIncome = Number(income);
    const amountPaid = annualTuition - accountBalance;
    const requestedAmount = Math.round(annualTuition * (0.2 + (index % 4) * 0.05));
    const awardAmount = Math.round(requestedAmount * (0.35 + (index % 3) * 0.15));
    const isNurseryKindergarten = gradeLevel === 'K' || gradeLevel === 'Pre-K';
    const isPre1To8 = /^[1-8]$/.test(gradeLevel);
    const scenarioIds = [`rr-family-cohort-${(index % 4) + 1}`, `rr-template-coverage-${String(index + 1).padStart(2, '0')}`];
    const rateCategory = accountBalance > 20000 ? 'high-follow-up' : accountBalance > 0 ? 'standard-follow-up' : 'settled';
    const enrollmentStatus = applicationStatuses[(index + 2) % applicationStatuses.length] ?? 'Applied';

    pushRecord(entities, record('families', familyId, {
      family_id: familyId,
      applicant_id: applicantId,
    }, {
      family_name: `${familyName} Family`,
      primary_contact: `${primaryFirstName} ${familyName}`,
      email: `${primaryFirstName.toLowerCase()}.${familyName.toLowerCase()}@northview.academy`,
      phone: `212-431-${phoneSuffix}`,
      address: `${100 + index} Cedar Lane, Lakewood, NJ 08701`,
      student_count: 2,
      enrollment_status: enrollmentStatus,
      enrollment_date: dateFor(index + 1, 6),
      rate_category: rateCategory,
      total_balance: accountBalance,
      has_financial_aid: index % 3 !== 0,
    }, scenarioIds, ['graph-family-spine']));

    pushRecord(entities, record('students', studentId, {
      family_id: familyId,
      student_id: studentId,
    }, {
      first_name: studentFirstName,
      last_name: familyName,
      date_of_birth: `20${String(12 - Math.min(index, 9)).padStart(2, '0')}-${String((index % 9) + 1).padStart(2, '0')}-14`,
      grade_level: gradeLevel,
      division,
      student_status: index === 15 ? 'Transferred' : 'Active',
      start_date: `2025-09-${String((index % 20) + 1).padStart(2, '0')}`,
    }, scenarioIds, ['demographics', 'grade-buckets']));
    pushRecord(entities, record('students', secondStudentId, {
      family_id: familyId,
      student_id: secondStudentId,
    }, {
      first_name: `${studentFirstName}a`,
      last_name: familyName,
      date_of_birth: `20${String(11 - Math.min(index, 8)).padStart(2, '0')}-${String((index % 8) + 2).padStart(2, '0')}-21`,
      grade_level: gradeLevel,
      division,
      student_status: 'Active',
      start_date: `2024-09-${String((index % 18) + 2).padStart(2, '0')}`,
    }, scenarioIds, ['demographics', 'grade-buckets']));
    studentIds.push(studentId, secondStudentId);

    pushRecord(entities, record('tuition_accounts', `rr-account-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
    }, {
      annual_tuition: annualTuition,
      total_charged: annualTuition + 1200,
      total_paid_ta: amountPaid,
      account_balance: accountBalance,
      account_status: accountBalance > 0 ? (accountBalance > 18000 ? 'Escalated' : 'Open') : 'Settled',
    }, scenarioIds, ['aging-pressure', 'fa-tuition-recon']));

    for (let installmentIndex = 0; installmentIndex < 4; installmentIndex += 1) {
      const installmentAmount = Math.round(annualTuition / 4);
      const paid = Math.max(0, installmentAmount - Math.max(0, accountBalance - installmentAmount * installmentIndex));
      const installmentStatus = statusCycle[(index + installmentIndex) % statusCycle.length] ?? 'Open';
      const installmentId = `rr-installment-${String(index + 1).padStart(3, '0')}-${installmentIndex + 1}`;
      pushRecord(entities, record('installments', installmentId, {
        family_id: familyId,
        installment_id: installmentId,
      }, {
        due_date: dateFor(installmentIndex + 7, 10 + (index % 8)),
        plan_name: installmentIndex % 2 === 0 ? 'Monthly plan' : 'Quarterly plan',
        installment_number: installmentIndex + 1,
        academic_year: installmentIndex < 2 ? '2025-2026' : '2026-2027',
        payment_status: installmentStatus,
        paid_date: paid > 0 ? dateFor(installmentIndex + 7, 24) : null,
        is_overdue: installmentStatus === 'Overdue',
        amount_due: installmentAmount,
        amount_paid: paid,
        balance_remaining: Math.max(0, installmentAmount - paid),
        late_fee_amount: installmentStatus === 'Overdue' ? 75 + (index % 4) * 25 : 0,
      }, scenarioIds, ['aging-pressure']));
    }

    for (let paymentIndex = 0; paymentIndex < 2; paymentIndex += 1) {
      const paymentId = `rr-payment-${String(index + 1).padStart(3, '0')}-${paymentIndex + 1}`;
      pushRecord(entities, record('payments', paymentId, {
        family_id: familyId,
        installment_id: `rr-installment-${String(index + 1).padStart(3, '0')}-${paymentIndex + 1}`,
      }, {
        payment_date: dateFor(paymentIndex + 8, 14 + (index % 6)),
        payment_amount: Math.round((annualTuition - accountBalance) / 3),
        payment_method: paymentMethods[(index + paymentIndex) % paymentMethods.length] ?? 'ACH',
        reference_number: `RR-${202600 + index * 7 + paymentIndex}`,
        allocated_to: paymentIndex === 0 ? 'Tuition' : 'Fees',
        payment_status_p: paymentIndex === 0 ? 'Posted' : 'Pending allocation',
      }, scenarioIds, ['collections-mix']));
    }

    for (let entryIndex = 0; entryIndex < 3; entryIndex += 1) {
      const entryType = entryIndex === 0 ? 'Charge' : entryIndex === 1 ? 'Payment' : 'Aid adjustment';
      const debit = entryType === 'Charge' ? Math.round(annualTuition / 2) : 0;
      const credit = entryType === 'Payment' ? Math.round(amountPaid / 2) : entryType === 'Aid adjustment' ? Math.round((annualTuition * ((index % 4) + 1)) / 100) : 0;
      pushRecord(entities, record('ledger_entries', `rr-ledger-${String(index + 1).padStart(3, '0')}-${entryIndex + 1}`, {
        family_id: familyId,
      }, {
        entry_date: dateFor(entryIndex + 7, 3 + (index % 10)),
        entry_type: entryType,
        description: `${entryType} for ${familyName} account ${entryIndex + 1}`,
        debit,
        credit,
        running_balance: Math.max(0, accountBalance + debit - credit),
      }, scenarioIds, ['tuition-cashflow']));
    }

    pushRecord(entities, record('contracts', `rr-contract-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
    }, {
      contract_year: '2026-2027',
      contract_status: contractStatus,
      signed_date: contractStatus === 'Signed' ? dateFor(index + 2, 8) : null,
      contract_amount: annualTuition,
    }, scenarioIds, ['contract-value']));

    pushRecord(entities, record('admissions_applications', `rr-application-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
      applicant_id: applicantId,
    }, {
      app_date: dateFor(index + 1, 2),
      app_status: applicationStatuses[index % applicationStatuses.length] ?? 'Applied',
      applying_grade: gradeLevel,
      entry_year: '2026-2027',
      docs_complete: index % 4 !== 0,
      missing_docs: index % 4 === 0 ? (index % 3) + 1 : 0,
    }, scenarioIds, ['application-pipeline', 'new-family-onboarding']));

    pushRecord(entities, record('enrollment_records', `rr-enrollment-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
      student_id: studentId,
      applicant_id: applicantId,
    }, {
      enrolled_grade: gradeLevel,
      enrolled_year: index % 2 === 0 ? '2025-2026' : '2026-2027',
      enrollment_type: index % 3 === 0 ? 'New' : 'Returning',
      enrollment_confirmed: index % 5 !== 0,
    }, scenarioIds, ['grade-year-concept']));

    pushRecord(entities, record('fa_applications', `rr-fa-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
      applicant_id: applicantId,
    }, {
      fa_ref_number: `FA-${2600 + index}`,
      fa_submission_date: dateFor(index + 1, 11),
      fa_status: faStatus,
      days_pending: (index % 6) * 7 + 2,
      reported_income: reportedIncome,
      income_per_child: Math.round(reportedIncome / 2),
      expense_ratio: Number((0.34 + (index % 6) * 0.04).toFixed(2)),
      system_rec_pct: 8 + (index % 7) * 5,
      confidence_level: ['Low', 'Medium', 'High'][index % 3] ?? 'Medium',
      override_amount: index % 4 === 0 ? 1800 + index * 120 : 0,
      flag_count: index % 5,
      high_severity_flags: index % 6 === 0 ? 1 : 0,
    }, scenarioIds, ['reported-income-bucket']));

    pushRecord(entities, record(
      YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
      `rr-scholarship-worksheet-${String(index + 1).padStart(3, '0')}`,
      { family_id: familyId },
      {
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName]: familyName,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenPre1To8]: isPre1To8 ? 2 : 0,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenNurseryKindergarten]: isNurseryKindergarten ? 2 : 0,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.contractAmount]: annualTuition,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedAmount]: requestedAmount,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedPercent]: requestedAmount / annualTuition,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.revisedContract]: annualTuition - awardAmount,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardAmount]: awardAmount,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardPercent]: awardAmount / annualTuition,
        [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.memo]: `Committee review cohort ${index + 1}`,
      },
      scenarioIds,
      ['scholarship-worksheet'],
    ));

    pushRecord(entities, record('notes', `rr-note-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
    }, {
      note_date: dateFor(index + 2, 18),
      note_author: 'Business Office',
      note_category: accountBalance > 0 ? 'Follow-up' : 'Account review',
      note_content: `${familyName} account reviewed for reporting cohort ${index + 1}.`,
    }, scenarioIds, ['tool-entity-ai-map']));

    pushRecord(entities, record('documents', `rr-document-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
      student_id: studentId,
      applicant_id: applicantId,
    }, {
      doc_name: `${familyName} ${docStatus}`,
      doc_type: index % 3 === 0 ? 'Tax return' : index % 3 === 1 ? 'Transcript' : 'Contract',
      upload_date: dateFor(index + 1, 16),
      doc_status: index % 4 === 0 ? 'Pending' : index % 6 === 0 ? 'Rejected' : 'Approved',
    }, scenarioIds, ['document-compliance']));
  });

  studentIds.forEach((studentId, index) => {
    const familyId = familyProfiles[Math.floor(index / 2)]?.[0] ?? 'rr-family-001';
    const awardAmount = index % 5 === 0 ? 0 : 1200 + (index % 9) * 850;
    pushRecord(entities, record('scholarships', `rr-scholarship-${String(index + 1).padStart(3, '0')}`, {
      family_id: familyId,
      student_id: studentId,
    }, {
      scholarship_name: index % 3 === 0 ? 'Need-based grant' : index % 3 === 1 ? 'Merit award' : 'Bridge award',
      award_amount: awardAmount,
      award_pct: awardAmount === 0 ? 0 : 5 + (index % 8) * 3,
      award_year: index % 2 === 0 ? '2025-2026' : '2026-2027',
      award_status: awardStatuses[index % awardStatuses.length] ?? 'Pending',
    }, [`rr-scholarship-cohort-${(index % 4) + 1}`], ['financial-aid-awards', 'fa-tuition-recon']));
  });

  attachTemplateIds(entities);
  return entities;
};

const hasUsableField = (
  entities: Record<string, RunReportsSeedEntityPayload>,
  template: ReportGenerationTemplateDefinition,
  fieldId: string,
): boolean =>
  template.entities.some((entityId) =>
    (entities[entityId]?.records ?? []).some((row) => {
      const value = row.cells[fieldId];
      return value !== null && value !== undefined && String(value).trim() !== '';
    }),
  );

const buildTemplateCoverage = (
  entities: Record<string, RunReportsSeedEntityPayload>,
): RunReportsSeedTemplateCoverage[] =>
  REPORT_GENERATION_GALLERY_TEMPLATES.map((template) => {
    const coveredFieldIds = template.fields.filter((fieldId) => hasUsableField(entities, template, fieldId));
    const sampleRecordIds = template.entities
      .flatMap((entityId) => entities[entityId]?.records.slice(0, 3).map((row) => row.id) ?? [])
      .slice(0, 6);
    return {
      templateId: template.id,
      templateName: template.name,
      moduleLabel: template.moduleLabel,
      entityIds: [...template.entities],
      fieldIds: [...template.fields],
      expectedMinimumRows: Math.max(1, sampleRecordIds.length),
      coveredFieldIds,
      missingFieldIds: template.fields.filter((fieldId) => !coveredFieldIds.includes(fieldId)),
      sampleRecordIds,
      chartUseCaseIds: visualUseCasesForTemplate(template.id),
    };
  });

const visualUseCasesForTemplate = (templateId: string): string[] => {
  const matches = RUN_REPORTS_SEED_VISUAL_COVERAGE.filter((coverage) => coverage.templateIds.includes(templateId));
  return matches.map((coverage) => coverage.expectedUseCaseId);
};

const uniqueStrings = (values: readonly (string | undefined)[]): string[] =>
  [...new Set(values.filter((value): value is string => Boolean(value)))];

const visualCoverageForMapping = (
  mapping: RunReportsTemplateVisualizationMapping,
): RunReportsSeedVisualCoverage => {
  const template = REPORT_GENERATION_GALLERY_TEMPLATES.find((entry) => entry.id === mapping.templateId);
  return {
    id: mapping.useCaseId,
    label: mapping.label,
    templateMappingId: mapping.id,
    primarySurface: mapping.primarySurface,
    fallbackSurface: mapping.fallbackSurface,
    templateIds: [mapping.templateId],
    entityIds: [...(template?.entities ?? [])],
    fieldIds: uniqueStrings([
      ...(template?.fields ?? []),
      ...mapping.dimensionFieldIds,
      ...mapping.groupFieldIds,
      ...mapping.metricFieldIds,
      ...mapping.requiredBucketFields,
      ...mapping.requiredMetricFields,
      ...mapping.visualEnrichmentFieldIds,
      mapping.xFieldId,
      mapping.yFieldId,
      ...mapping.unsafeLabelFieldIds,
    ]),
    chartTypes: [...mapping.chartTypes],
    advancedVisuals: [...mapping.advancedVisuals],
    expectedUseCaseId: mapping.useCaseId,
    aggregate: mapping.aggregate,
    dimensionFieldIds: [...mapping.dimensionFieldIds],
    groupFieldIds: [...mapping.groupFieldIds],
    requiredBucketFields: [...mapping.requiredBucketFields],
    requiredMetricFields: [...mapping.requiredMetricFields],
    visualEnrichmentFieldIds: [...mapping.visualEnrichmentFieldIds],
    unsafeLabelFieldIds: [...mapping.unsafeLabelFieldIds],
    minBuckets: mapping.minBuckets,
    minSeries: mapping.minSeries,
  };
};

export const RUN_REPORTS_SEED_VISUAL_COVERAGE: RunReportsSeedVisualCoverage[] =
  RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS.map(visualCoverageForMapping);

const buildAssistantActionCoverage = (): RunReportsSeedAssistantActionCoverage[] => [
  {
    actionId: 'run-reports-build-from-aging-report',
    label: 'Build from Aging Report',
    route: '/school/run-reports',
    requiredTemplateIds: ['aging-report'],
    requiredEntityIds: ['installments', 'families'],
    requiredFieldIds: ['family_name', 'due_date', 'amount_due', 'balance_remaining', 'is_overdue', 'academic_year'],
    expectedRouteMutation: 'apply template and replace report definition',
    responseStructure: { kind: 'route-command', requiresWorkspaceMutation: true, receiptIsProof: false },
  },
  {
    actionId: 'run-reports-add-collection-risk-filters',
    label: 'Add collection risk filters',
    route: '/school/run-reports',
    requiredTemplateIds: ['aging-report', 'outstanding-balances'],
    requiredEntityIds: ['installments', 'tuition_accounts', 'families'],
    requiredFieldIds: ['balance_remaining', 'account_balance', 'payment_status'],
    expectedRouteMutation: 'add balance and overdue filters to current report definition',
    responseStructure: { kind: 'route-command', requiresWorkspaceMutation: true, receiptIsProof: false },
  },
  {
    actionId: 'run-reports-choose-columns',
    label: 'Choose export columns',
    route: '/school/run-reports',
    requiredTemplateIds: ['aging-report', 'payment-history'],
    requiredEntityIds: ['installments', 'payments', 'families'],
    requiredFieldIds: ['family_name', 'due_date', 'balance_remaining', 'amount_due', 'payment_status', 'plan_name', 'academic_year'],
    expectedRouteMutation: 'replace selected field set with export-ready columns',
    responseStructure: { kind: 'route-command', requiresWorkspaceMutation: true, receiptIsProof: false },
  },
  {
    actionId: 'run-reports-show-best-visual',
    label: 'Show recommended charts',
    route: '/school/run-reports',
    requiredTemplateIds: ['aging-report', 'avg-by-income', 'enrollment-conversion'],
    requiredEntityIds: ['installments', 'families', 'fa_applications', 'scholarships', 'enrollment_records'],
    requiredFieldIds: ['due_date', 'academic_year', 'payment_status', 'balance_remaining', 'amount_due', 'amount_paid', 'plan_name', 'family_name', 'reported_income', 'system_rec_pct', 'award_pct', 'enrolled_grade', 'enrolled_year'],
    expectedRouteMutation: 'open Visualization tab and select chartable fields with non-degenerate buckets',
    responseStructure: { kind: 'route-command', requiresWorkspaceMutation: true, receiptIsProof: false },
  },
];

const buildToolEntityAiMappings = () => [
  {
    mappingId: 'tuition-risk-workbench',
    templateIds: ['aging-report', 'outstanding-balances', 'tuition-ledger-review', 'contract-status'],
    entityIds: ['families', 'installments', 'tuition_accounts', 'ledger_entries', 'contracts'],
    tableView: 'family balance queue with due dates, ledger entries, and contract state',
    visualView: 'aging pressure, cashflow, and contract value charts',
    graphView: 'family -> account -> installment/payment/ledger/contract relationship graph',
    aiQuickActionIds: ['run-reports-build-from-aging-report', 'run-reports-add-collection-risk-filters', 'run-reports-show-best-visual'],
  },
  {
    mappingId: 'financial-aid-review-workbench',
    templateIds: ['apps-by-status', 'awards-by-grade', 'total-committed', 'avg-by-income', 'processing-time', 'doc-compliance', 'override-frequency', 'appeal-outcomes'],
    entityIds: ['families', 'students', 'fa_applications', 'scholarships', 'documents'],
    tableView: 'application status, award, income, override, and document compliance rows',
    visualView: 'income bucket, award by grade, processing time, and document status charts',
    graphView: 'family -> student -> aid application -> scholarship/document graph',
    aiQuickActionIds: ['run-reports-show-best-visual'],
  },
  {
    mappingId: 'admissions-enrollment-workbench',
    templateIds: ['app-pipeline', 'enrollment-conversion', 'incomplete-docs', 'new-family-onboarding'],
    entityIds: ['families', 'students', 'admissions_applications', 'enrollment_records', 'contracts', 'documents'],
    tableView: 'application pipeline, missing documents, enrollment conversion, and contract handoff rows',
    visualView: 'pipeline status and grade-year conversion charts',
    graphView: 'family -> applicant -> enrollment -> contract/document graph',
    aiQuickActionIds: ['run-reports-show-best-visual', 'run-reports-choose-columns'],
  },
];

const buildOperationCoverage = (entities: Record<string, RunReportsSeedEntityPayload>) => [
  {
    operationId: 'previewAssistantReportGeneration',
    boundedContext: 'AssistantPlatform',
    transportField: 'assistantPlatformQuery',
    seedEntityIds: Object.values(entities).flatMap((entity) => entity.records.slice(0, 3).map((row) => row.id)),
    proofRequirement: 'AppSync preview readback must return non-empty rows for every Gallery template.',
  },
  {
    operationId: 'listAssistantReportTemplates',
    boundedContext: 'AssistantPlatform',
    transportField: 'assistantPlatformQuery',
    seedEntityIds: REPORT_GENERATION_GALLERY_TEMPLATES.map((template) => template.id),
    proofRequirement: 'Gallery template enumeration must match the shared semantic catalog.',
  },
];

const buildRouteIntentCoverage = () => [
  {
    route: '/school/run-reports' as const,
    module: 'run-reports' as const,
    widget: 'Templates Gallery',
    visibleContract: 'All 22 Gallery templates are selectable and source-backed.',
    backingEntities: REPORT_GENERATION_ENTITIES.map((entity) => entity.id),
    seedScenarioIds: ['rr-template-coverage-01', 'rr-template-coverage-08', 'rr-template-coverage-13'],
  },
  {
    route: '/school/run-reports' as const,
    module: 'run-reports' as const,
    widget: 'Filters entity tree',
    visibleContract: 'All 13 entity types expose join-aware selectable fields.',
    backingEntities: REPORT_GENERATION_ENTITIES.map((entity) => entity.id),
    seedScenarioIds: ['rr-family-cohort-1', 'rr-family-cohort-2'],
  },
  {
    route: '/school/run-reports' as const,
    module: 'run-reports' as const,
    widget: 'Visualization and Advanced visuals',
    visibleContract: 'Chartable field combinations produce useful bucketed charts and 3D visual states.',
    backingEntities: ['installments', 'payments', 'ledger_entries', 'contracts', 'fa_applications', 'scholarships', 'enrollment_records'],
    seedScenarioIds: ['rr-family-cohort-3', 'rr-family-cohort-4'],
  },
];

const buildNonProdWritePlan = (): RunReportsSeedBundle['nonProdWritePlan'] => ({
  defaultMode: 'dry-run',
  guardEnv: 'RUN_REPORTS_SEED_ALLOW_WRITES',
  confirmationFlag: '--confirm',
  destructiveResetDefault: false,
  loadOrder: [
    'families',
    'students',
    'tuition_accounts',
    'installments',
    'payments',
    'ledger_entries',
    'contracts',
    'admissions_applications',
    'enrollment_records',
    'fa_applications',
    'scholarships',
    'documents',
    'notes',
  ],
  appPlans: [
    {
      appId: 'intellio-demo',
      status: 'adapter-ready',
      writeSurface: 'non-prod store loader handoff for students-families, admissions, and financial-aid backing stores',
      readbackSurface: 'assistantPlatformQuery.previewAssistantReportGeneration on /school/run-reports',
      notes: [
        'Use the single public AppSync head for readback proof.',
        'Direct store writes are load mechanics only and cannot promote readiness.',
      ],
    },
    {
      appId: 'ystnj',
      status: 'handoff-required',
      writeSurface: 'extend scripts/automated/seed-ystnj-unified after bundle validation',
      readbackSurface: 'existing YSTNJ seeded-data and Run Reports route gates',
      notes: [
        'Keep YSTNJ unified seed schema stable; add Run Reports coverage as a governed extension.',
        'Do not replace the unified seed bundle with app-local fixtures.',
      ],
    },
  ],
});

const findDeniedDataMarkers = (value: string): string[] =>
  DENIED_DATA_PATTERNS.flatMap((pattern) => {
    const match = value.match(pattern);
    return match ? [match[0]] : [];
  });

const nonEmptyCellValuesForField = (
  records: readonly RunReportsSeedRecord[],
  fieldId: string,
): RunReportsSeedPrimitive[] =>
  records
    .map((row) => row.cells[fieldId])
    .filter((value): value is RunReportsSeedPrimitive =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '',
    );

const distinctCellValueCount = (
  records: readonly RunReportsSeedRecord[],
  fieldId: string,
): number => new Set(nonEmptyCellValuesForField(records, fieldId).map((value) => String(value))).size;

const hasNumericCellValue = (
  records: readonly RunReportsSeedRecord[],
  fieldId: string,
): boolean =>
  nonEmptyCellValuesForField(records, fieldId).some((value) => {
    if (typeof value === 'number') {
      return Number.isFinite(value);
    }
    if (typeof value !== 'string') {
      return false;
    }
    return Number.isFinite(Number(value.replace(/[$,%\s,]/g, '')));
  });

const distinctNumericPairs = (
  records: readonly RunReportsSeedRecord[],
  xFieldId: string,
  yFieldId: string,
): number => new Set(
  records.flatMap((row) => {
    const x = row.cells[xFieldId];
    const y = row.cells[yFieldId];
    return x !== null &&
      x !== undefined &&
      y !== null &&
      y !== undefined &&
      String(x).trim() !== '' &&
      String(y).trim() !== ''
      ? [`${x}:${y}`]
      : [];
  }),
).size;

const buildValidationResult = (
  bundle: Omit<RunReportsSeedBundle, 'validation'> | RunReportsSeedBundle,
): RunReportsSeedValidationResult => {
  const issues: RunReportsSeedValidationIssue[] = [];
  const mappingById = new Map(RUN_REPORTS_TEMPLATE_VISUALIZATION_MAPPINGS.map((mapping) => [mapping.id, mapping]));
  const parsed = RunReportsSeedBundleSchema.omit({ validation: true }).safeParse(bundle);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      issues.push({
        severity: 'error',
        code: 'schema-invalid',
        message: `${issue.path.join('.') || '(root)'}: ${issue.message}`,
      });
    }
  }

  for (const issue of validateRunReportsVisualizationMappings()) {
    issues.push({
      severity: issue.severity,
      code: issue.code,
      message: issue.message,
    });
  }

  const templateIds = new Set(bundle.templateCoverage.map((entry) => entry.templateId));
  for (const template of REPORT_GENERATION_GALLERY_TEMPLATES) {
    if (!templateIds.has(template.id)) {
      issues.push({
        severity: 'error',
        code: 'missing-template-coverage',
        message: `Missing template coverage for ${template.id}.`,
      });
    }
  }

  const visualTemplateIds = new Set(bundle.visualCoverage.flatMap((coverage) => coverage.templateIds));
  for (const template of REPORT_GENERATION_GALLERY_TEMPLATES) {
    if (!visualTemplateIds.has(template.id)) {
      issues.push({
        severity: 'error',
        code: 'missing-template-visual-coverage',
        message: `Missing visual coverage for ${template.id}.`,
      });
    }
  }

  for (const coverage of bundle.templateCoverage) {
    if (coverage.missingFieldIds.length > 0) {
      issues.push({
        severity: 'error',
        code: 'template-field-uncovered',
        message: `${coverage.templateId} missing field data: ${coverage.missingFieldIds.join(', ')}.`,
      });
    }
    if (coverage.expectedMinimumRows < 1 || coverage.sampleRecordIds.length === 0) {
      issues.push({
        severity: 'error',
        code: 'template-empty',
        message: `${coverage.templateId} has no sample records.`,
      });
    }
  }

  for (const entity of REPORT_GENERATION_ENTITIES) {
    const records = bundle.entities[entity.id]?.records ?? [];
    if (records.length === 0) {
      issues.push({
        severity: 'error',
        code: 'entity-empty',
        message: `${entity.id} has no records.`,
      });
    }
  }

  const familyIds = new Set((bundle.entities.families?.records ?? []).map((row) => row.joinKeys.family_id).filter(Boolean));
  const studentIds = new Set((bundle.entities.students?.records ?? []).map((row) => row.joinKeys.student_id).filter(Boolean));
  for (const entity of Object.values(bundle.entities)) {
    for (const row of entity.records) {
      const familyId = row.joinKeys.family_id;
      if (entity.entityId !== 'families' && familyId && !familyIds.has(familyId)) {
        issues.push({
          severity: 'error',
          code: 'join-family-missing',
          message: `${row.id} references missing family_id ${familyId}.`,
        });
      }
      const studentId = row.joinKeys.student_id;
      if (studentId && !studentIds.has(studentId)) {
        issues.push({
          severity: 'error',
          code: 'join-student-missing',
          message: `${row.id} references missing student_id ${studentId}.`,
        });
      }
      const deniedMarkers = Object.values({ ...row.joinKeys, ...row.cells })
        .flatMap((value) => findDeniedDataMarkers(String(value ?? '')));
      if (deniedMarkers.length > 0) {
        issues.push({
          severity: 'error',
          code: 'denied-data-marker',
          message: `${row.id} contains denied data marker(s): ${[...new Set(deniedMarkers)].join(', ')}.`,
        });
      }
    }
  }

  for (const visual of bundle.visualCoverage) {
    const uncovered = [...visual.requiredBucketFields, ...visual.requiredMetricFields]
      .filter((fieldId) => !visual.fieldIds.includes(fieldId));
    if (uncovered.length > 0) {
      issues.push({
        severity: 'error',
        code: 'visual-field-uncovered',
        message: `${visual.id} missing required field declaration: ${uncovered.join(', ')}.`,
      });
    }
    const unsafeBucketFields = [...visual.requiredBucketFields]
      .filter((fieldId) => RUN_REPORTS_UNSAFE_CHART_LABEL_FIELD_IDS.includes(fieldId));
    if (unsafeBucketFields.length > 0) {
      issues.push({
        severity: 'error',
        code: 'visual-unsafe-bucket-field',
        message: `${visual.id} uses unsafe chart bucket fields: ${unsafeBucketFields.join(', ')}.`,
      });
    }
    if (visual.primarySurface === 'visual') {
      const records = visual.entityIds.flatMap((entityId) => bundle.entities[entityId]?.records ?? []);
      const bucketDistinctCount = Math.max(
        0,
        ...visual.requiredBucketFields.map((fieldId) => distinctCellValueCount(records, fieldId)),
      );
      const groupDistinctCount = Math.max(
        0,
        ...visual.groupFieldIds.map((fieldId) => distinctCellValueCount(records, fieldId)),
      );
      const mapping = mappingById.get(visual.templateMappingId);
      const scatterDistinctCount = mapping?.xFieldId && mapping.yFieldId
        ? distinctNumericPairs(records, mapping.xFieldId, mapping.yFieldId)
        : 0;
      const metricDistinctCount = Math.max(
        0,
        ...visual.requiredMetricFields.map((fieldId) => distinctCellValueCount(records, fieldId)),
      );
      if (
        visual.minBuckets > 0 &&
        Math.max(bucketDistinctCount, scatterDistinctCount, metricDistinctCount) < visual.minBuckets
      ) {
        issues.push({
          severity: 'error',
          code: 'visual-buckets-degenerate',
          message: `${visual.id} has fewer than ${visual.minBuckets} useful chart buckets.`,
        });
      }
      if (visual.minSeries > 1 && groupDistinctCount < visual.minSeries) {
        issues.push({
          severity: 'error',
          code: 'visual-series-degenerate',
          message: `${visual.id} has fewer than ${visual.minSeries} useful series values.`,
        });
      }
      for (const metricFieldId of visual.requiredMetricFields) {
        if (!hasNumericCellValue(records, metricFieldId)) {
          issues.push({
            severity: 'error',
            code: 'visual-metric-empty',
            message: `${visual.id} has no numeric seed values for required metric ${metricFieldId}.`,
          });
        }
      }
    }
  }

  for (const template of REPORT_GENERATION_GALLERY_TEMPLATES) {
    for (const entityId of template.entities) {
      if (!entityDefinitionById.has(entityId)) {
        issues.push({
          severity: 'error',
          code: 'unknown-template-entity',
          message: `${template.id} references unknown entity ${entityId}.`,
        });
      }
    }
  }

  const joinEntityIds = new Set(Object.keys(REPORT_GENERATION_ENTITY_JOINS));
  for (const entity of REPORT_GENERATION_ENTITIES) {
    if (entity.id !== 'families' && !joinEntityIds.has(entity.id) && entity.joinFields?.length) {
      issues.push({
        severity: 'warning',
        code: 'join-map-partial',
        message: `${entity.id} declares join fields but has no top-level join map entry.`,
      });
    }
  }

  const recordCount = Object.values(bundle.entities).reduce((sum, entity) => sum + entity.records.length, 0);
  const errorCount = issues.filter((issue) => issue.severity === 'error').length;
  return {
    status: errorCount === 0 ? 'PASS' : 'FAIL',
    issueCount: issues.length,
    issues,
    summary: {
      templateCount: bundle.templateCoverage.length,
      entityCount: Object.keys(bundle.entities).length,
      recordCount,
      targetApps: [...bundle.targetApps],
    },
  };
};

export type BuildRunReportsSeedBundleInput = {
  generatedAt?: string;
  runId?: string;
  targetApps?: readonly RunReportsSeedTargetApp[];
};

export const buildRunReportsSeedBundle = (
  input: BuildRunReportsSeedBundleInput = {},
): RunReportsSeedBundle => {
  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const runId = input.runId ?? `run-reports-seed-${generatedAt.replace(/[:.]/g, '-')}`;
  const targetApps = input.targetApps?.length ? [...input.targetApps] : [...RUN_REPORTS_SEED_TARGET_APPS];
  const entities = buildSeedEntities();
  const withoutValidation: Omit<RunReportsSeedBundle, 'validation'> = {
    schemaVersion: RUN_REPORTS_SEED_SCHEMA_VERSION,
    artifactType: RUN_REPORTS_SEED_ARTIFACT_TYPE,
    generatedAt,
    runId,
    targetApps,
    academicYears: ['2025-2026', '2026-2027'],
    source: {
      catalogTemplateCount: REPORT_GENERATION_GALLERY_TEMPLATES.length,
      catalogEntityCount: REPORT_GENERATION_ENTITIES.length,
      protocolRefs: PROTOCOL_REFS,
    },
    entities,
    templateCoverage: buildTemplateCoverage(entities),
    visualCoverage: RUN_REPORTS_SEED_VISUAL_COVERAGE,
    assistantActionCoverage: buildAssistantActionCoverage(),
    toolEntityAiMappings: buildToolEntityAiMappings(),
    operationCoverage: buildOperationCoverage(entities),
    routeIntentCoverage: buildRouteIntentCoverage(),
    nonProdWritePlan: buildNonProdWritePlan(),
  };
  return {
    ...withoutValidation,
    validation: buildValidationResult(withoutValidation),
  };
};

export const validateRunReportsSeedBundle = (
  value: unknown,
): RunReportsSeedValidationResult => {
  const parsed = RunReportsSeedBundleSchema.safeParse(value);
  if (!parsed.success) {
    return {
      status: 'FAIL',
      issueCount: parsed.error.issues.length,
      issues: parsed.error.issues.map((issue) => ({
        severity: 'error',
        code: 'schema-invalid',
        message: `${issue.path.join('.') || '(root)'}: ${issue.message}`,
      })),
      summary: {
        templateCount: 0,
        entityCount: 0,
        recordCount: 0,
        targetApps: [],
      },
    };
  }
  return buildValidationResult(parsed.data);
};
